import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { FC } from "react";

export interface NameMatch {
  id: number | string;
  name: string;
  accountConfigVendorName?: string; // Only for stores - from Account Config table
}

export interface DuplicateNameDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed?: () => void;
  matchType: "exact" | "fuzzy";
  entityType: "vendor" | "store";
  proposedName: string;
  matches: NameMatch[];
}

export const DuplicateNameDialog: FC<DuplicateNameDialogProps> = ({
  open,
  onClose,
  onProceed,
  matchType,
  entityType,
  proposedName,
  matches,
}) => {
  const isExactMatch = matchType === "exact";
  const entityLabel = entityType === "vendor" ? "Customer" : "Store";

  const title = isExactMatch ? `${entityLabel} Name Already Exists` : `Similar ${entityLabel} Names Found`;

  const message = isExactMatch
    ? `The ${entityType} name "${proposedName}" already exists in the system. Please choose a different name.`
    : `The ${entityType} name "${proposedName}" is similar to existing ${entityType}s. Please review the list below and confirm if you want to proceed with this name.`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="duplicate-name-dialog-title">
      <DialogTitle id="duplicate-name-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>

        {matches.length > 0 && (
          <>
            <DialogContentText sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
              {isExactMatch ? "Existing:" : "Similar names:"}
            </DialogContentText>
            <List dense>
              {matches.map(match => (
                <ListItem key={match.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={match.name}
                    secondary={
                      match.accountConfigVendorName ? `Account Config: ${match.accountConfigVendorName}` : undefined
                    }
                    primaryTypographyProps={{
                      fontFamily: "monospace",
                      fontSize: "0.95rem",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 2, backgroundColor: "white" }}>
        <Button
          onClick={onClose}
          className="contained"
          color="error"
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        {!isExactMatch && onProceed && (
          <Button onClick={onProceed} color="error" variant="contained" sx={{ textTransform: "none" }}>
            Proceed Anyway
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
